import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/Functions';

const Hero = ({ onOpenConsultation }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', captcha_code: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/captcha`);
      const data = await response.json();
      if (data.status === 'success') {
        setCaptchaToken(data.captcha_token);
        setCaptchaImage(data.image_base64);
        setFormData(prev => ({ ...prev, captcha_code: '' }));
      }
    } catch (error) {
      console.error('Failed to load captcha:', error);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    if (name === 'name') {
      // Only allow alphabets and spaces
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    
    if (name === 'phone') {
      const cleaned = value.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');
      const maxLen = cleaned.startsWith('+') ? 15 : 10;
      if (cleaned.length > maxLen) return;
      finalValue = cleaned;
    }
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    if (name === 'name' && errors['full_name']) {
      setErrors(prev => ({ ...prev, full_name: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.phone && phoneDigits.length < 10) {
      setErrors({ phone: 'Phone number must be at least 10 digits.' });
      return;
    }
    
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        captcha_token: captchaToken,
        captcha_code: formData.captcha_code
      };
      
      const response = await fetch(`${API_URL}/api/public/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const res = await response.json();
      
      if (!response.ok) {
        if (res.errors) {
          console.log('[DEBUG] Validation errors:', res.errors);
          const flatErrors = {};
          Object.keys(res.errors).forEach(key => { flatErrors[key] = res.errors[key][0]; });
          setErrors(flatErrors);
          if (flatErrors.captcha_code) refreshCaptcha();
        } else {
          console.log('[DEBUG] Error response:', res.message);
          alert(res.message || 'Failed to send message. Please try again later.');
        }
        setSubmitting(false);
        return;
      }
      
      console.log('[DEBUG] Form Data Stored on DB successfully:', res.data);
      console.log('[DEBUG] Email Status:', res.email_status);
      
      const userName = formData.name;
      setFormData({ name: '', email: '', phone: '', message: '', captcha_code: '' });
      navigate('/thank-you', { state: { name: userName } });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const points = [
    'PAN India Services',
    'Experienced Compliance Professionals',
    '100% Online Process',
    'Transparent Pricing',
    'Fast Turnaround Time',
    'End-to-End Documentation Support'
  ];

  return (
    <section id="home" className="hero-section position-relative bg-white" style={{ display: 'flex', alignItems: 'center' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .hero-section {
          padding-top: 80px;
          padding-bottom: 100px;
          font-family: 'Inter', sans-serif;
          overflow: hidden;
          text-align: left;
        }
        
        .hero-eyebrow {
          font-size: 14px;
          font-weight: 700;
          color: #CFA047;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          border-left: 4px solid #CFA047;
          padding-left: 14px;
          margin-bottom: 20px;
          line-height: 1.2;
        }
        
        .hero-title {
          font-size: 46px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1.25;
          margin-bottom: 24px;
          max-width: 900px;
          letter-spacing: -1.5px;
        }

        .hero-title span {
          color: #061830;
          background: linear-gradient(120deg, #061830 0%, #CFA047 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero-sub {
          font-size: 17px;
          color: #475569;
          line-height: 1.8;
          margin-bottom: 36px;
          max-width: 720px;
        }

        .hero-service-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 24px;
        }

        .hero-service-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #f8fafc;
          color: #334155;
          font-size: 14px;
          font-weight: 600;
          border-radius: 50px;
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          cursor: default;
        }

        .hero-service-pill i {
          color: #CFA047;
          font-size: 14px;
        }

        .hero-service-pill:hover {
          border-color: #CFA047;
          color: #061830;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(207, 160, 71, 0.15);
          background: #fff;
        }

        .hero-points-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 28px;
          max-width: 650px;
          margin-bottom: 44px;
        }

        @media (max-width: 991px) {
          .hero-title { font-size: 38px; }
          .hero-section { padding-top: 100px; padding-bottom: 80px; }
        }

        @media (max-width: 767px) {
          .hero-points-grid { grid-template-columns: 1fr; gap: 14px; }
          .hero-title { font-size: 32px; }
          .hero-section { padding-top: 80px; padding-bottom: 60px; }
        }

        .hero-point-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 15.5px;
          font-weight: 600;
          color: #1e293b;
        }

        .hero-point-check {
          width: 24px;
          height: 24px;
          background: #CFA047;
          color: #061830;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          flex-shrink: 0;
          border: 1px solid rgba(207, 160, 71, 0.5);
        }

        .hero-cta-group {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .hero-cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 36px;
          background: #061830;
          color: white;
          border-radius: 50px;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 10px 24px rgba(6, 24, 48, 0.25);
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
          border: none;
          cursor: pointer;
        }

        .hero-cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px rgba(6, 24, 48, 0.35);
          background: #CFA047;
        }

        .hero-cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 36px;
          background: transparent;
          color: #1e293b;
          border-radius: 50px;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          border: 2px solid #cbd5e1;
          transition: all 0.2s;
          cursor: pointer;
        }

        .hero-cta-secondary:hover {
          border-color: #0f172a;
          background-color: #f8fafc;
          color: #0f172a;
        }

        .hero-right-img {
          width: 100%;
          border-radius: 28px;
          box-shadow: 0 24px 48px rgba(0,0,0,0.1);
          transform: perspective(1000px) rotateY(-5deg) translateY(0);
          transition: transform 0.6s ease;
          border: 8px solid #fff;
        }
        
        .hero-right-wrap {
          position: relative;
        }

        .hero-right-wrap:hover .hero-right-img {
          transform: perspective(1000px) rotateY(0deg) translateY(-8px);
        }

        /* Inline Form Styles */
        .hero-form-card {
          background: #ffffff;
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 28px 56px rgba(0,0,0,0.13);
          border: 1px solid rgba(207, 160, 71, 0.18);
          position: relative;
          z-index: 2;
          overflow: hidden;
        }
        .hero-form-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; width: 100%; height: 5px;
          background: linear-gradient(90deg, #061830 0%, #CFA047 60%, #061830 100%);
        }
        .hero-form-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f1f5f9;
        }
        .hero-form-icon-wrap {
          width: 48px; height: 48px;
          border-radius: 14px;
          background: linear-gradient(135deg, #061830 0%, #CFA047 100%);
          display: flex; align-items: center; justify-content: center;
          color: white;
          font-size: 20px;
          flex-shrink: 0;
          box-shadow: 0 6px 16px rgba(207, 160, 71, 0.3);
        }
        .hero-form-title {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 2px;
          letter-spacing: -0.4px;
        }
        .hero-form-subtitle {
          font-size: 13px;
          color: #94a3b8;
          margin: 0;
        }
        .hero-form-body {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .hero-field-group {
          position: relative;
          display: flex;
          align-items: center;
        }
        .hero-field-group--textarea {
          align-items: flex-start;
        }
        .hero-field-icon {
          position: absolute;
          left: 16px;
          color: #94a3b8;
          font-size: 14px;
          z-index: 1;
          pointer-events: none;
          transition: color 0.2s;
        }
        .hero-field-icon--top {
          top: 14px;
        }
        .hero-field-group:focus-within .hero-field-icon {
          color: #CFA047;
        }
        .hero-form-input {
          width: 100%;
          padding: 13px 16px 13px 42px;
          border: 1.5px solid #e8edf4;
          border-radius: 12px;
          font-size: 14.5px;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
          background-color: #f8fafc;
          color: #1e293b;
          margin-bottom: 0;
        }
        .hero-form-input::placeholder {
          color: #b0bccc;
        }
        .hero-form-input:focus {
          border-color: #CFA047;
          box-shadow: 0 0 0 4px rgba(207, 160, 71, 0.1);
          background-color: #ffffff;
        }
        .hero-form-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: linear-gradient(135deg, #061830 0%, #0d2647 100%);
          color: white;
          border: none;
          width: 100%;
          padding: 15px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 8px 24px rgba(6, 24, 48, 0.3);
          letter-spacing: 0.3px;
          margin-top: 4px;
        }
        .hero-form-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px rgba(6, 24, 48, 0.4);
          background: linear-gradient(135deg, #CFA047 0%, #b8860b 100%);
        }
        .hero-form-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }
        .hero-btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: heroSpin 0.7s linear infinite;
          display: inline-block;
        }
        @keyframes heroSpin { to { transform: rotate(360deg); } }
        .hero-form-privacy {
          font-size: 12px;
          color: #94a3b8;
          text-align: center;
          margin: 4px 0 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .hero-form-success {
          text-align: center;
          padding: 24px 16px;
        }
        .hero-form-success-icon {
          width: 72px; height: 72px;
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          color: #10b981;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 32px;
          margin: 0 auto 20px;
          border: 3px solid #a7f3d0;
          box-shadow: 0 12px 24px rgba(16, 185, 129, 0.2);
        }
        .hero-form-success-title {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 8px;
        }
        .hero-form-success-msg {
          font-size: 14px;
          color: #64748b;
          max-width: 260px;
          margin: 0 auto;
        }

        /* Decorative background elements */
        .hero-blob-1 {
          position: absolute;
          top: -120px; right: -120px;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(207,160,71,0.08) 0%, rgba(255,255,255,0) 70%);
          border-radius: 50%;
          z-index: 0;
          pointer-events: none;
        }
        
        .hero-blob-2 {
          position: absolute;
          bottom: -120px; left: -120px;
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(6,24,48,0.06) 0%, rgba(255,255,255,0) 70%);
          border-radius: 50%;
          z-index: 0;
          pointer-events: none;
        }
      `}</style>

      {/* Decorative Blobs */}
      <div className="hero-blob-1"></div>
      <div className="hero-blob-2"></div>

      <div className="container position-relative z-1">
        <div className="row align-items-center g-5">
          
          {/* ── Left Content ── */}
          {/* ── Left Content ── */}
          <div className="col-lg-7 text-start">
            <div className="hero-eyebrow">
              Business Registration &amp; Compliance Services Across India
            </div>
            
            <h1 className="hero-title">
              One Platform for All Your <span>Business Registration Needs</span>
            </h1>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '16px', fontSize: '16px' }}>
                Get expert assistance for:
              </div>
              <div className="hero-service-pills">
                {['GST Registration', 'Trademark Registration', 'Company Registration', 'ISO Certification', 'FSSAI Registration', 'IEC Registration'].map((service, i) => (
                  <div key={i} className="hero-service-pill">
                    <i className="fa-solid fa-check-circle"></i> {service}
                  </div>
                ))}
              </div>
            </div>

            <p className="hero-sub" style={{ marginBottom: '28px', lineHeight: '1.6' }}>
              Whether you're starting a new business, protecting your brand, obtaining business licenses, or ensuring regulatory compliance, Legal Infos provides reliable, affordable, and hassle-free solutions with complete online support across India.
            </p>

            <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '20px' }}>
              Why Choose Legal Infos?
            </h5>

            <div className="hero-points-grid">
              {points.map((pt, i) => (
                <div key={i} className="hero-point-item">
                  <div className="hero-point-check">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <div>{pt}</div>
                </div>
              ))}
            </div>

            <div className="hero-cta-group">
              <button type="button" onClick={() => onOpenConsultation()} className="hero-cta-primary">
                Get Free Consultation <i className="fa-solid fa-arrow-right ms-2"></i>
              </button>
              <button type="button" onClick={() => onOpenConsultation()} className="hero-cta-secondary">
                Talk to an Expert <i className="fa-solid fa-phone-volume ms-2"></i>
              </button>
            </div>
          </div>

          {/* ── Right Content / Form ── */}
          <div className="col-lg-5" id="hero-lead-form">
            <div className="hero-form-card">
              {/* Card Header */}
              <div className="hero-form-header">
                <div className="hero-form-icon-wrap">
                  <i className="fa-solid fa-scale-balanced"></i>
                </div>
                <div>
                  <h4 className="hero-form-title">Free Consultation</h4>
                  <p className="hero-form-subtitle">Our experts will reach out within 15 minutes.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="hero-form-body">
                  <div className="hero-field-group">
                    <div className="hero-field-icon"><i className="fa-solid fa-user"></i></div>
                    <input
                      type="text"
                      name="name"
                      required
                      className="hero-form-input"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.full_name && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-8px', marginBottom: '12px', marginLeft: '12px' }}>{errors.full_name}</div>}
                  <div className="hero-field-group">
                    <div className="hero-field-icon"><i className="fa-solid fa-envelope"></i></div>
                    <input
                      type="email"
                      name="email"
                      required
                      className="hero-form-input"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.email && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-8px', marginBottom: '12px', marginLeft: '12px' }}>{errors.email}</div>}
                  <div className="hero-field-group">
                    <div className="hero-field-icon"><i className="fa-solid fa-phone"></i></div>
                    <input
                      type="text"
                      name="phone"
                      className="hero-form-input"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.phone && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-8px', marginBottom: '12px', marginLeft: '12px' }}>{errors.phone}</div>}
                  <div className="hero-field-group hero-field-group--textarea">
                    <div className="hero-field-icon hero-field-icon--top"><i className="fa-solid fa-message"></i></div>
                    <textarea
                      name="message"
                      required
                      rows="4"
                      className="hero-form-input"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={handleChange}
                      style={{ resize: 'none' }}
                    ></textarea>
                  </div>
                  {errors.message && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-8px', marginBottom: '12px', marginLeft: '12px' }}>{errors.message}</div>}
                  <div className="hero-field-group">
                    <div className="hero-field-icon"><i className="fa-solid fa-shield-halved"></i></div>
                    <input
                      type="text"
                      name="captcha_code"
                      required
                      className="hero-form-input"
                      placeholder="Enter Captcha"
                      value={formData.captcha_code}
                      onChange={handleChange}
                      style={{ letterSpacing: '2px', paddingRight: '130px' }}
                    />
                    <div style={{ position: 'absolute', right: '6px', top: '5px', bottom: '5px', display: 'flex', gap: '4px' }}>
                      {captchaImage && (
                        <img 
                          src={captchaImage} 
                          alt="captcha" 
                          style={{ height: '100%', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', width: '80px', objectFit: 'contain' }} 
                        />
                      )}
                      <button 
                        type="button" 
                        onClick={refreshCaptcha}
                        disabled={captchaLoading}
                        style={{ border: 'none', background: '#f1f5f9', width: '38px', borderRadius: '6px', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <i className={`fa-solid fa-rotate-right ${captchaLoading ? 'fa-spin' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                  {errors.captcha_code && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-10px', marginLeft: '12px', marginBottom: '8px' }}>{errors.captcha_code}</div>}
                  <button type="submit" className="hero-form-btn" disabled={submitting}>
                    {submitting ? (
                      <><span className="hero-btn-spinner"></span> Sending...</>
                    ) : (
                      <>Send Message &nbsp;<i className="fa-solid fa-paper-plane"></i></>
                    )}
                  </button>
                  <p className="hero-form-privacy"><i className="fa-solid fa-lock"></i> Your information is 100% confidential.</p>
                </form>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default Hero;
